/* eslint-disable react/no-array-index-key */
import { useFormContext } from 'react-hook-form';
import { observer } from 'mobx-react';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import TextField from './TextField';
import ReadOnly from './ReadOnly';
import TextArea from './TextArea';
import DatePicker from './DatePicker';
import NumericTextField from './NumericTextField';
import FieldWrapperVertical from './FieldWrapperVertical';
import { useMasterData } from '../../providers/AppDataProvider/index';
import Switch from './Switch';
import { useLocalization } from '../../providers/LocalizationProvider';

function FieldGridRenderer({ schema, renderField }) {
  if (!schema || !Array.isArray(schema)) return null;

  // Group fields by row
  const rows = schema.reduce((acc, field) => {
    acc[field.row] = acc[field.row] || [];
    acc[field.row].push(field);
    return acc;
  }, {});

  // Sort rows by row number
  const sortedRows = Object.keys(rows)
    .sort((a, b) => Number(a) - Number(b))
    .map(rowNum => rows[rowNum]);

  return sortedRows.map((fieldsInRow, rowIdx) => {
    const isLastRow = rowIdx === sortedRows.length - 1;

    return (
      <Grid2 container key={rowIdx} gap={2} sx={{ mb: isLastRow ? 0 : 2.5 }}>
        {fieldsInRow.map((field, colIdx) => {
          const style = field?.width || 'grow';
          return (
            <Grid2 xs={12} md={style} key={colIdx}>
              {renderField(field)}
            </Grid2>
          );
        })}
      </Grid2>
    );
  });
}

function FieldGenerator({
  schema,
  customComponents = {},
  permissions = {},
  noLabels = false,
}) {
  const {
    register,
    formState: { errors },
    getValues,
  } = useFormContext();
  const { formatMessage } = useLocalization();
  const masterData = useMasterData();

  const renderField = field => {
    const placeHolderText = field?.placeholder?.localeId
      ? formatMessage({
          id: field?.placeholder?.localeId,
          defaultMessage: field?.placeholder?.defaultMessage,
        })
      : '';

    let commonProps = {
      id: field?.name,
    };

    if (!(field?.register === false)) {
      commonProps = {
        ...commonProps,
        permission: field?.permission || null,
        placeholder: placeHolderText,
        error: errors[field?.name]?.message || false,
        ...register(field?.name),
      };
    }

    const commonFieldWrapperProps = {
      field,
      errors,
      customComponents,
      noLabels,
    };

    switch (field?.control) {
      case 'subsection': {
        return (
          <FieldWrapperVertical key={field?.name} {...commonFieldWrapperProps}>
            {FieldGridRenderer({ schema: field?.children, renderField })}
          </FieldWrapperVertical>
        );
      }
      case 'text': {
        return (
          <FieldWrapperVertical key={field?.name} {...commonFieldWrapperProps}>
            <TextField {...commonProps} type={field?.type || 'text'} />
          </FieldWrapperVertical>
        );
      }
      case 'date': {
        let commonDateProps = {};
        if (field?.disablePast) {
          commonDateProps = {
            ...commonDateProps,
            disablePast: field?.disablePast,
          };
        }
        if (field?.disableFuture) {
          commonDateProps = {
            ...commonDateProps,
            disableFuture: field?.disableFuture,
          };
        }
        return (
          <FieldWrapperVertical key={field?.name} {...commonFieldWrapperProps}>
            <DatePicker {...commonProps} {...commonDateProps} />
          </FieldWrapperVertical>
        );
      }
      case 'textarea': {
        return (
          <FieldWrapperVertical key={field?.name} {...commonFieldWrapperProps}>
            <TextArea {...commonProps} rows={field.rows || 4} />
          </FieldWrapperVertical>
        );
      }

      case 'number': {
        const {
          rows: numberRows,
          positiveNumber: isPositiveNumber,
          allowNull: canAllowNull,
          ...numericTextFieldProps
        } = field;
        return (
          <FieldWrapperVertical key={field?.name} {...commonFieldWrapperProps}>
            <NumericTextField
              {...commonProps}
              {...numericTextFieldProps}
              rows={numberRows || 4}
              positiveNumber={isPositiveNumber || false}
              allowNull={canAllowNull || false}
            />
          </FieldWrapperVertical>
        );
      }

      case 'switch': {
        return (
          <FieldWrapperVertical key={field?.name} {...commonFieldWrapperProps}>
            <Switch {...commonProps} />
          </FieldWrapperVertical>
        );
      }

      case 'readonly': {
        const value = getValues(field?.name);
        return (
          <FieldWrapperVertical key={field?.name} {...commonFieldWrapperProps}>
            <ReadOnly id={field?.name} {...register(field?.name)}>
              {value}
            </ReadOnly>
          </FieldWrapperVertical>
        );
      }

      case 'custom': {
        const CustomComponent = customComponents[field.customComponent];

        // Adding form element permissions to only required components
        // due to {...props} it will render in custom components on UI
        switch (field.customComponent) {
          case 'Dispatch':
          case 'AddressValidator':
            commonProps = {
              ...commonProps,
              formPermissions: permissions,
            };
            break;
          default:
        }

        if (CustomComponent) {
          return (
            <FieldWrapperVertical
              key={field?.name}
              {...commonFieldWrapperProps}>
              <CustomComponent {...commonProps} field={field} />
            </FieldWrapperVertical>
          );
        }

        const CustomComponentConfig =
          customComponents.CustomComponentConfigs(masterData)?.[
            field.customComponent
          ] || {};

        if (CustomComponentConfig) {
          switch (CustomComponentConfig?.control) {
            case 'autocomplete': {
              const { AutoCompleteWrapper } = customComponents;
              return (
                <FieldWrapperVertical
                  key={field?.name}
                  {...commonFieldWrapperProps}>
                  <AutoCompleteWrapper
                    {...commonProps}
                    field={field}
                    customConfig={CustomComponentConfig}
                  />
                </FieldWrapperVertical>
              );
            }
            case 'autocompletev2': {
              const { AutoCompleteWrapperV2 } = customComponents;
              return (
                <FieldWrapperVertical
                  key={field?.name}
                  {...commonFieldWrapperProps}>
                  <AutoCompleteWrapperV2
                    {...commonProps}
                    field={field}
                    customConfig={CustomComponentConfig}
                  />
                </FieldWrapperVertical>
              );
            }
            case 'SelectStatic': {
              const { SelectStatic } = customComponents;
              return (
                <FieldWrapperVertical
                  key={field?.name}
                  {...commonFieldWrapperProps}>
                  <SelectStatic
                    {...commonProps}
                    field={field}
                    customConfig={CustomComponentConfig}
                  />
                </FieldWrapperVertical>
              );
            }
            case 'StatesSelector': {
              const { StatesSelector } = customComponents;
              return (
                <FieldWrapperVertical
                  key={field?.name}
                  {...commonFieldWrapperProps}>
                  <StatesSelector
                    {...commonProps}
                    field={field}
                    customConfig={CustomComponentConfig}
                  />
                </FieldWrapperVertical>
              );
            }
            default:
              return `No custom definition for ${field.name} with control: ${CustomComponentConfig?.control}`;
          }
        }

        return `No component definition for ${field.control}`;
      }
      default:
        return null;
    }
  };

  return FieldGridRenderer({ schema, renderField });
}

export default observer(FieldGenerator);
